/**
 * MypageRateContainer
 * --
 */
import React from 'react';
import MypageRatePresenter from './MypageRatePresenter';
import { useDefaultShopDataStack, useUserAction, useUserStack } from 'store';
import { useGetGrades } from 'hooks/GradeHooks';
import { useGetMemberShipGrades } from 'hooks/MemberShipGradeHooks';
import dayjs from 'dayjs';
import { useGetStatisticsSummary } from 'hooks/StatisticsHooks';
import MessageAlert from 'utils';
import { useUpdateUser } from 'hooks/UserHooks';
import { FileManager } from 'utils/FileManager';

const MypageRateContainer = (props) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  const { screenSize } = props;

  /* ===== Mutate ===== */
  const { updateUser } = useUpdateUser();

  /* ===== Store ===== */
  const userInfo = useUserStack();
  const userAction = useUserAction();
  const { settings } = useDefaultShopDataStack();

  // 합산 기준일
  const GradeStandard = React.useMemo(() => {
    if (!settings) return;

    const { grade_setting } = settings;

    const standardDay = dayjs()
      .subtract(grade_setting.total_month_standard, 'month')
      .set('date', grade_setting.date_of_change)
      .startOf('day')
      .format('YYYY-MM-DD');

    return standardDay;
  }, [settings]);

  /* ===== Data ===== */
  // 등급 데이터
  const { data: Grades } = useGetGrades(
    {
      is_enable: 'Y',
    },
    {
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return null;
        else return data;
      },
      initialData: [],
    }
  );
  // 등급 내역
  const { data: GradeHistories } = useGetMemberShipGrades(
    {
      user_id: userInfo.user_id,
    },
    {
      enabled: !!userInfo,
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return [];
        else return data;
      },
      initialData: [],
    }
  );
  // 구매 건수, 구매 금액
  const { data: Summary } = useGetStatisticsSummary(
    'user-total-purchase',
    {
      userId: userInfo.user_id,
      startDate: GradeStandard,
      endDate: dayjs().format('YYYY-MM-DD'),
    },
    {
      enabled: !!(userInfo && GradeStandard),
      select: (res) => {
        const { status, data } = res;

        if (status !== 200)
          return {
            total_count: 0,
            total_price: 0,
          };
        else return data;
      },
      initialData: {
        total_count: 0,
        total_price: 0,
      },
    }
  );

  /* ===== STATE ===== */
  const [isLoading, setIsLoading] = React.useState(false);

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**
   * 사업자 회원 신청
   * --
   * @param {string} bizNum
   * @param {string | File} bizFile
   * @param {string} type
   */
  const handleSubmit = async (bizNum, bizFile, type = 'create') => {
    const msg =
      type === 'create'
        ? '사업자 회원으로 신청하시겠습니까?\n심사는 최대 7일정도 소요될 수 있습니다.'
        : '사업자 정보를 수정하시겠습니까?\n수정 시 재심사를 거쳐야하며, 반영되기까지 일정 기간이 소요될 수 있습니다.';

    const confirm = window.confirm(msg);

    if (!confirm) return;

    try {
      setIsLoading(true);

      let upload;
      if (type === 'create') {
        const { status: uploadStatus, data: uploadData } =
          await FileManager.upload(`users/${userInfo?.user_id}`, bizFile);

        if (uploadStatus !== 200) {
          MessageAlert.info('파일 업로드에 실패하였습니다.');
          return;
        }

        upload = uploadData?.file_name;
      } else {
        // 파일을 새로 등록했는지 여부
        const isNew = typeof bizFile === 'string' ? false : true;

        // 기존 등록된 파일 유지 시
        if (!isNew) {
          upload = bizFile;
        } else {
          const { status: uploadStatus, data: uploadData } =
            await FileManager.upload(userInfo?.user_id, bizFile);

          if (uploadStatus !== 200) {
            MessageAlert.info('파일 업로드에 실패하였습니다.');
            return;
          }

          upload = uploadData?.file_name;
        }
      }

      const updateData = {
        user_id: userInfo?.user_id,
        business_num: bizNum,
        business_num_url: upload,
        business_level: 1,
      };

      const { status, data } = await updateUser(updateData);

      if (status !== 200) {
        MessageAlert.info('사업자 회원 신청에 실패하였습니다.');
        return;
      }

      MessageAlert.success('신청되었습니다. 빠른 시일 내에 답변드리겠습니다.');
      userAction.setUser(data);
    } catch (err) {
      console.error(`[handleSubmit] Error !! ${err}`);
      MessageAlert.error('사업자 신청에 실패하였습니다!');
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 100);
    }
  };

  /* ===== HOOKS ===== */
  /**
   * 회원 등급 정제
   * --
   */
  const UserGrade = React.useMemo(() => {
    // 역순 정렬
    const sortHistory = [...GradeHistories].sort(
      (a, b) => b.created_at - a.created_at
    );
    // 같은 등급 처음 달성일 추출
    const fid = sortHistory.findIndex(
      (history) => history.grade_id !== userInfo.grade_id
    );
    const lastIndex = fid === -1 ? sortHistory.length - 1 : fid;

    const userData = {
      ...userInfo,
      grade: Grades?.find((grade) => grade.grade_id === userInfo.grade_id),
      grade_start_date: sortHistory?.[lastIndex]?.created_at,
      grade_end_date: dayjs().unix(),
    };

    return userData;
  }, [userInfo, GradeHistories, Grades]);

  /**
   * 등급 리스트 정제
   * --
   */
  const GradeList = React.useMemo(() => {
    if (!Grades?.length) {
      return {
        variable: [],
        fixed: [],
      };
    }

    const gradeList = Grades?.reduce(
      (acc, cur) => {
        const { is_fixed } = cur;

        if (is_fixed === 'Y') {
          acc['fixed'].push(cur);
        } else {
          acc['variable'].push(cur);
        }

        return acc;
      },
      {
        variable: [],
        fixed: [],
      }
    );

    return gradeList;
  }, [Grades]);

  /* ===== RENDER ===== */
  return (
    <>
      <MypageRatePresenter
        user={UserGrade}
        grades={GradeList}
        totalPrice={Summary?.total_price}
        totalCount={Summary?.total_count}
        setting={settings?.grade_setting}
        screenSize={screenSize}
        isLoading={isLoading}
        onSubmit={handleSubmit}
      />
    </>
  );
};

export default MypageRateContainer;
